import React, { useEffect, useRef, useState } from 'react';
import { Film, Upload, X, AlertTriangle } from 'lucide-react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { storage } from '../../firebase';

// Same list the MCP course-video tools accept (functions/mcp/courseVideos.js).
const ACCEPTED_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'];
const ACCEPT_ATTR = '.mp4,.mov,.m4v,.webm,video/mp4,video/quicktime,video/webm,video/x-m4v';
const MAX_BYTES = 5 * 1024 * 1024 * 1024; // 5 GB

export const formatVideoLength = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

const formatBytes = (bytes) =>
  bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(2)} GB` : `${(bytes / 1024 ** 2).toFixed(1)} MB`;

/** Read the duration off the file locally — doubles as a "can this browser play it" check. */
const probeVideo = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    const done = (result) => {
      URL.revokeObjectURL(url);
      resolve(result);
    };
    video.preload = 'metadata';
    video.onloadedmetadata = () => done({ seconds: video.duration, playable: true });
    video.onerror = () => done({ seconds: null, playable: false });
    setTimeout(() => done({ seconds: null, playable: null }), 15000);
    video.src = url;
  });

const isAcceptedFile = (file) => {
  if (ACCEPTED_TYPES.includes(file.type)) return true;
  return /\.(mp4|mov|m4v|webm)$/i.test(file.name || '');
};

/**
 * Lesson video picker for the CRM course editors. Uploads to Storage as soon as a
 * file is chosen (resumable, with progress and cancel) and reports the download
 * URL + detected length through onChange; the lesson itself is still saved by
 * the editor's Save button.
 */
export default function CourseVideoUploader({ courseId, lessonId, videoUrl, darkMode, disabled, onChange, onUploadingChange }) {
  const inputRef = useRef(null);
  const taskRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [upload, setUpload] = useState(null); // { name, size, transferred }
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);

  useEffect(() => () => taskRef.current?.cancel(), []);

  const setUploading = (value) => onUploadingChange?.(value);

  const startUpload = async (file) => {
    setError(null);
    setWarning(null);
    if (!isAcceptedFile(file)) {
      setError('Pick an MP4, MOV, M4V or WebM video.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`That file is ${formatBytes(file.size)} — the limit is 5 GB.`);
      return;
    }
    const auth = getAuth();
    if (!auth.currentUser) {
      setError('You are signed out. Sign in again to upload.');
      return;
    }

    const probe = await probeVideo(file);
    if (probe.playable === false) {
      setWarning("This browser can't play this file, so clients may not be able to either. Export it as an H.264 MP4 if it doesn't play below.");
    }

    const safeName = String(file.name || 'video').replace(/[^\w.-]+/g, '_');
    const path = `course-videos/${courseId}/${lessonId || 'new'}/${Date.now()}_${safeName}`;
    const task = uploadBytesResumable(ref(storage, path), file, { contentType: file.type || 'video/mp4' });
    taskRef.current = task;
    setUpload({ name: file.name, size: file.size, transferred: 0 });
    setUploading(true);

    task.on(
      'state_changed',
      (snap) => setUpload((u) => (u ? { ...u, transferred: snap.bytesTransferred } : u)),
      (err) => {
        taskRef.current = null;
        setUpload(null);
        setUploading(false);
        if (err.code !== 'storage/canceled') setError(`Upload failed: ${err.message}`);
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          onChange?.({ videoUrl: url, videoLength: formatVideoLength(probe.seconds) });
        } catch (err) {
          setError(`Uploaded, but could not get the video link: ${err.message}`);
        } finally {
          taskRef.current = null;
          setUpload(null);
          setUploading(false);
        }
      }
    );
  };

  const handleFiles = (files) => {
    const file = files?.[0];
    if (file && !disabled && !upload) startUpload(file);
  };

  const pct = upload ? Math.round((upload.transferred / Math.max(upload.size, 1)) * 100) : 0;
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div>
      {videoUrl && !upload && (
        <div className="mb-3">
          <video key={videoUrl} controls src={videoUrl} className="w-full rounded-md bg-black" style={{ maxHeight: '420px' }} />
        </div>
      )}

      {upload ? (
        <div className={`rounded-lg border p-4 ${darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className={`flex items-center gap-2 text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Film className="h-4 w-4 shrink-0" />
              <span className="truncate">{upload.name}</span>
            </span>
            <button
              type="button"
              onClick={() => taskRef.current?.cancel()}
              className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 shrink-0"
            >
              <X className="h-4 w-4" /> Cancel
            </button>
          </div>
          <div className={`h-2 w-full rounded-full overflow-hidden ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
            <div className="h-full bg-red-600 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className={`mt-1 text-xs text-center ${muted}`}>
            {pct}% · {formatBytes(upload.transferred)} of {formatBytes(upload.size)}
          </p>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !disabled && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={`rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            dragOver
              ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
              : darkMode
                ? 'border-gray-600 hover:border-gray-500 bg-gray-700/40'
                : 'border-gray-300 hover:border-gray-400 bg-white'
          }`}
        >
          <Upload className={`mx-auto h-6 w-6 mb-2 ${muted}`} />
          <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            {videoUrl ? 'Replace video' : 'Upload video'} — drop a file here or click to choose
          </p>
          <p className={`text-xs mt-1 ${muted}`}>MP4, MOV, M4V or WebM · up to 5 GB · MP4 (H.264) plays everywhere</p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT_ATTR}
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      )}

      {videoUrl && !upload && !disabled && (
        <div className="mt-2 text-center">
          <button type="button" onClick={() => onChange?.({ videoUrl: '', videoLength: '' })} className={`text-xs underline ${muted} hover:text-red-600`}>
            Remove video from this lesson
          </button>
        </div>
      )}

      {warning && (
        <p className="mt-2 flex items-start justify-center gap-1.5 text-sm text-amber-600 dark:text-amber-400 text-center">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          {warning}
        </p>
      )}
      {error && <p className="mt-2 text-sm text-red-600 text-center">{error}</p>}
    </div>
  );
}
